"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getCondensedActionGraph = getCondensedActionGraph;
var _functions = require("../utility/functions");
var _heap = require("../utility/heap");
var _actionOutcomes = require("./actionOutcomes");
var _notableEvents = require("./notableEvents");
var _fifoQueue = require("../utility/fifoQueue");
/**
 * `GraphState` is used when creating the condensedActionGraph. When an action is placed into a condensedActionGraph node, it can update
 * the `GraphState` (analogous to our reducers in mission-server) and also modify the actions which are pending to be placed in the current
 * condensedActionGraph node (ie. the actions which are expected to run on the condensedActionGraph node).
 *
 * Note that the current algorithm which generates the condensedActionGraph makes the assumption that when a condensedActionGraph node
 * is referenced, it will always start with the same `GraphState` and `pendingActions` (both carried over from the end state of a [parent] previous condensedActionGraph node).
 *
 */

/**
 * getCondensedActionGraph - get graph forms of the simulation.
 *
 * @param {IdType} initialActionId - the id of the actions which starts the simulation
 * @param {$ReadOnlyArray<GameAction<IdType>>} actions - an array containing all actions in the simulation
 *
 * @return {{
 *	condensedActionGraph: CondensedActionGraph<IdType>, - this graph contains nodes which act as distinct paths the simulations can take and edges describing the possible path a simulation can take after taking the current node's path
 *	actionGraph: ActionGraph<IdType>, - this graph contains nodes which represent single actions with edges denoting which actions may be ran as a result of other actions
 *}}
 */
function getCondensedActionGraph(initialActionId, actions) {
  // calculate the actionGraph using a breadthFirst search
  const actionMap = (0, _functions.toIdMapFrom_id)(actions);
  const breadthFirstQueue = new _fifoQueue.FIFOQueue([{
    timeFromStart: 0,
    actionId: initialActionId,
    depthFromStart: 0
  }]);
  const childActionToParentActions = {}; // having an entry in this map means that we have seen the action before
  childActionToParentActions[String(initialActionId)] = {};
  const actionGraph = {};
  const actionsNotInGraph = new Set(actions.map(action => String(action._id)));
  const referencedActionsThatDoNotExist = new Set();
  const endingActions = [];
  const deathActions = [];
  let currentActionData;
  while (currentActionData = breadthFirstQueue.shift()) {
    const stringActionId = String(currentActionData.actionId);
    actionsNotInGraph.delete(stringActionId);
    const currentAction = actionMap[stringActionId];
    if (!currentAction) {
      referencedActionsThatDoNotExist.add(stringActionId);
      continue;
    }
    const outcomeData = (0, _actionOutcomes.getNextActionIdsFromActionWithBranches)(currentAction);
    const isEndAction = currentAction.type === 'END_MISSION';
    if (isEndAction) {
      endingActions.push(stringActionId);
    }
    const isDeathAction = currentAction.type === 'MISSION_EFFECT' && currentAction.effectId === 'DEATH';
    if (isDeathAction) {
      deathActions.push(stringActionId);
    }
    const distinctOutcomes = (outcomeData.outcomes.length ? outcomeData.outcomes : [{
      id: 'default',
      actionIds: []
    }]).map(outcome => {
      const specialRules = [];
      const actionData = [];
      for (let prefixGroup of outcomeData.prefix) {
        specialRules.push(prefixGroup.specialEdgeRules);
        actionData.push(prefixGroup);
      }
      for (let optionalGroup of outcomeData.optionals) {
        // ASSUMPTION: all optional actions are ran during the action
        for (let optionActionData of optionalGroup.actionIds) {
          actionData.push(optionActionData);
          specialRules.push(optionActionData.specialEdgeRules);
        }
      }
      for (let actionInfo of outcome.actionIds) {
        actionData.push(actionInfo);
        specialRules.push(actionInfo.specialEdgeRules);
      }

      // make sure all referenced actions actually exist
      const actionDataWithActionsThatDoNotExistRemoved = [];
      for (let actionInfo of actionData) {
        const stringActionId = String(actionInfo.actionId);
        if (actionMap[stringActionId] == null) {
          referencedActionsThatDoNotExist.add(stringActionId);
          continue;
        }
        actionDataWithActionsThatDoNotExistRemoved.push(actionInfo);
      }
      return {
        outcomeId: outcome.id,
        actionData: actionDataWithActionsThatDoNotExistRemoved,
        specialRules: (0, _actionOutcomes.mergeSpecialEdgeRules)(specialRules)
      };
    });
    const canReachEndAfterSteps = isEndAction ? {
      stepCount: 0,
      actionId: stringActionId
    } : null;
    const canReachDeathAfterSteps = isDeathAction ? {
      stepCount: 0,
      actionId: stringActionId
    } : null;
    actionGraph[stringActionId] = {
      timeFromStart: currentActionData.timeFromStart,
      distinctOutcomes: distinctOutcomes.map(outcome => ({
        ...outcome,
        canReachEndAfterSteps,
        canReachDeathAfterSteps
      })),
      isEndAction,
      isDeathAction,
      actionId: currentAction._id,
      depthFromStart: currentActionData.depthFromStart,
      canReachEndAfterSteps,
      canReachDeathAfterSteps,
      graphStateUpdater: ACTION_TO_CONDENSED_STATE_UPDATER_MAP[currentAction.type] ?? null
    };
    for (const outcome of distinctOutcomes) {
      for (const {
        actionId: childActionId,
        startTime
      } of outcome.actionData) {
        const stringChildActionId = String(childActionId);
        if (!childActionToParentActions.hasOwnProperty(stringChildActionId)) {
          childActionToParentActions[stringChildActionId] ??= {};
          breadthFirstQueue.push({
            actionId: childActionId,
            timeFromStart: currentActionData.timeFromStart + startTime,
            depthFromStart: currentActionData.depthFromStart + 1
          });
        }
        childActionToParentActions[stringChildActionId][stringActionId] ??= new Set();
        childActionToParentActions[stringChildActionId][stringActionId].add(outcome.outcomeId);
      }
    }
  }
  populateMinStepsToTargetActions(actionGraph, endingActions, childActionToParentActions, 'canReachEndAfterSteps');
  populateMinStepsToTargetActions(actionGraph, deathActions, childActionToParentActions, 'canReachDeathAfterSteps');

  // calculate the condensed action graph
  const condensedActionGraph = {};
  const startingCondensedNodeId = getStartingCondensedNodeId(String(initialActionId));
  const breadthFirstCondensedGraphQueue = new _fifoQueue.FIFOQueue([{
    condensedNodeId: startingCondensedNodeId,
    startTime: 0,
    isStartNode: true,
    startingActions: [{
      actionId: initialActionId,
      startTime: 0,
      spawnedByAction: null
    }],
    depthFromStart: 0,
    graphState: {
      currentScreenActionId: null
    }
  }]);
  const seenCondensedNodeIds = new Set([startingCondensedNodeId]);
  const condensedNodeIdsContainingEndMissionActions = [];
  const condensedNodeIdsContainingDeathActions = [];
  const childCondensedNodesToParentCondensedNodesMap = {};
  let condensedNodeData;
  while (condensedNodeData = breadthFirstCondensedGraphQueue.shift()) {
    const depthFromStart = condensedNodeData.depthFromStart;
    const seenActionsInCondensedNode = new Set([]);
    const minStartTimeActionHeap = (0, _heap.generateMinHeap)([], (a, b) => a.startTime - b.startTime);
    const condensedNodeActionsInOrder = [];
    const childCondensedNodes = new Map();
    let durationUntilEndAction = null; // null if there is no death action in the condensedNode
    let durationUntilDeathAction = null; // null if there is no death action in the condensedNode

    for (const actionData of condensedNodeData.startingActions) {
      const stringActionId = String(actionData.actionId);
      if (seenActionsInCondensedNode.has(stringActionId)) {
        continue;
      }
      minStartTimeActionHeap.push(actionData);
      seenActionsInCondensedNode.add(stringActionId);
    }
    const pendingActionsWithMultipleOutcomes = [];
    while (!minStartTimeActionHeap.empty()) {
      const nextItem = minStartTimeActionHeap.peek();
      if (nextItem == null || pendingActionsWithMultipleOutcomes.length &&
      // considering all actions occurring at the same time as the action with multiple outcomes as occurring before that action
      nextItem.startTime > pendingActionsWithMultipleOutcomes[0].timeFromStartOfCondensedNode) {
        break;
      }
      minStartTimeActionHeap.pop(); // actually remove the item from the heap
      condensedNodeActionsInOrder.push(nextItem);
      const {
        startTime,
        actionId
      } = nextItem;
      const stringActionId = String(actionId);
      const actionData = actionGraph[stringActionId];
      if (actionData.isEndAction && durationUntilEndAction == null) {
        durationUntilEndAction = startTime;
      }
      if (actionData.isDeathAction && durationUntilDeathAction == null) {
        durationUntilDeathAction = startTime;
      }
      if (actionData.graphStateUpdater) {
        actionData.graphStateUpdater(actionData.actionId, condensedNodeData.graphState, minStartTimeActionHeap);
      }
      if (actionData.distinctOutcomes.length === 0) {
        continue;
      } else if (actionData.distinctOutcomes.length === 1 && !actionData.distinctOutcomes[0].specialRules // if there are special rules, then we must make an edge for those rules to be applied to. Create a new condensedNode.
      ) {
        // only one possible way this action can advance
        for (const childActionData of actionData.distinctOutcomes[0].actionData) {
          if (seenActionsInCondensedNode.has(String(childActionData.actionId))) {
            continue;
          }
          minStartTimeActionHeap.push({
            actionId: childActionData.actionId,
            startTime: startTime + childActionData.startTime,
            // translate time from start of parent action to start of condensed node
            spawnedByAction: actionData.actionId
          });
          seenActionsInCondensedNode.add(String(childActionData.actionId));
        }
      } else {
        pendingActionsWithMultipleOutcomes.push({
          timeFromStartOfCondensedNode: startTime,
          actionData,
          spawnedByAction: actionData.actionId
        }); // there are multiple possible outcomes for this action, consider each of these outcomes in a different condensedNode. Handles all actions (with single outcomes) occurring at the same time as this action before handling the outcomes of this action.
      }
    }
    let condensedNodeDuration = 0;
    const pendingActionWithMultipleOutcomes = pendingActionsWithMultipleOutcomes[0];
    if (pendingActionWithMultipleOutcomes) {
      const outcomeStartTime = pendingActionWithMultipleOutcomes.timeFromStartOfCondensedNode;
      condensedNodeDuration = outcomeStartTime;
      const remainingActions = [...pendingActionsWithMultipleOutcomes.slice(1).map(({
        actionData,
        timeFromStartOfCondensedNode: actionStartTime,
        spawnedByAction
      }) => ({
        actionId: actionData.actionId,
        startTime: actionStartTime - outcomeStartTime,
        // move the start time so 0 now correlates with the startTime of the condensedNode of this outcome
        spawnedByAction
      })), ...minStartTimeActionHeap.toArray().map(({
        actionId,
        startTime: actionStartTime,
        spawnedByAction
      }) => ({
        actionId,
        startTime: actionStartTime - outcomeStartTime,
        // move the start time so 0 now correlates with the startTime of the condensedNode of this outcome
        spawnedByAction
      }))]; // Assumption: Any time this outcome occurs, it has the same pending actions

      // multiple outcomes, create a condensedNode for each possible outcome
      for (const outcome of pendingActionWithMultipleOutcomes.actionData.distinctOutcomes) {
        const condensedNodeId = getCondensedNodeId(String(pendingActionWithMultipleOutcomes.actionData.actionId), outcome.outcomeId);
        const specialRules = childCondensedNodes.get(condensedNodeId) ?? [];
        if (outcome.specialRules) {
          specialRules.push(outcome.specialRules);
        }
        childCondensedNodes.set(condensedNodeId, specialRules);
        childCondensedNodesToParentCondensedNodesMap[condensedNodeId] ??= {};
        childCondensedNodesToParentCondensedNodesMap[condensedNodeId][condensedNodeData.condensedNodeId] ??= [];
        childCondensedNodesToParentCondensedNodesMap[condensedNodeId][condensedNodeData.condensedNodeId].push(outcome.specialRules);
        if (seenCondensedNodeIds.has(condensedNodeId)) {
          // we have seen this outcome before, do not try to recalculate to avoid an infinite loop
          continue;
        }
        seenCondensedNodeIds.add(condensedNodeId);
        breadthFirstCondensedGraphQueue.push({
          condensedNodeId,
          startTime: condensedNodeData.startTime + condensedNodeDuration,
          startingActions: [...outcome.actionData.map(({
            actionId,
            startTime
          }) => ({
            actionId,
            startTime,
            spawnedByAction: pendingActionWithMultipleOutcomes.spawnedByAction
          })), ...remainingActions],
          isStartNode: false,
          depthFromStart: depthFromStart + 1,
          graphState: {
            ...condensedNodeData.graphState
          }
        });
      }
    } else {
      condensedNodeDuration = condensedNodeActionsInOrder.length ? condensedNodeActionsInOrder[condensedNodeActionsInOrder.length - 1].startTime : 0;
    }
    if (durationUntilEndAction != null) {
      condensedNodeIdsContainingEndMissionActions.push(condensedNodeData.condensedNodeId);
    }
    if (durationUntilDeathAction != null) {
      condensedNodeIdsContainingDeathActions.push(condensedNodeData.condensedNodeId);
    }
    const criticalPathActions = {};
    const actionsWithOnlyStartTime = [];
    for (let [index, actionData] of condensedNodeActionsInOrder.entries()) {
      actionsWithOnlyStartTime.push({
        actionId: actionData.actionId,
        startTime: actionData.startTime
      });
      const action = actionGraph[String(actionData.actionId)];
      if (!action || action.canReachEndAfterSteps == null) {
        continue;
      }
      criticalPathActions[String(action.actionId)] = index;
    }
    const childNodes = [];
    for (let [childNodeId, specialRules] of childCondensedNodes.entries()) {
      childNodes.push({
        childNodeId,
        specialRules: (0, _actionOutcomes.mergeSpecialEdgeRules)(specialRules)
      });
    }
    condensedActionGraph[condensedNodeData.condensedNodeId] = {
      timeFromStart: condensedNodeData.startTime,
      actions: actionsWithOnlyStartTime,
      notableEvents: (0, _notableEvents.generateNotableEvents)(actionsWithOnlyStartTime, actionMap, condensedNodeData.isStartNode, initialActionId),
      childNodes,
      isStartingNode: condensedNodeData.isStartNode,
      containsEndAction: durationUntilEndAction != null,
      containsDeathAction: durationUntilDeathAction != null,
      depthFromStart: condensedNodeData.depthFromStart,
      canReachEndAfterSteps: null,
      canReachDeathAfterSteps: null,
      duration: condensedNodeDuration,
      pessimisticDurationUntilEnd: durationUntilEndAction != null ? {
        duration: durationUntilEndAction,
        nextNodeId: null
      } : null,
      pessimisticDurationUntilDeath: durationUntilDeathAction != null ? {
        duration: durationUntilDeathAction,
        nextNodeId: null
      } : null,
      criticalPathActions: criticalPathActions
    };
  }
  const childCondensedNodesToParentCondensedNodes = {};
  for (let childNodeId in childCondensedNodesToParentCondensedNodesMap) {
    childCondensedNodesToParentCondensedNodes[childNodeId] ??= [];
    const childNodeMap = childCondensedNodesToParentCondensedNodesMap[childNodeId];
    for (let parentNodeId in childNodeMap) {
      childCondensedNodesToParentCondensedNodes[childNodeId].push({
        parentNodeId,
        specialRules: (0, _actionOutcomes.mergeSpecialEdgeRules)(childNodeMap[parentNodeId])
      });
    }
  }
  populateMinStepsToTargetCondensedNodes(condensedNodeIdsContainingEndMissionActions, condensedActionGraph, childCondensedNodesToParentCondensedNodes, 'canReachEndAfterSteps', 'breakOnEndPath' // this makes it so that edges after onCheatDeath are not traversed when calculating pessimistic times until the end of mission. Which also causes it to be treated as a death and jump back to checkpoint during timeline calculations.
  );
  populateMinStepsToTargetCondensedNodes(condensedNodeIdsContainingDeathActions, condensedActionGraph, childCondensedNodesToParentCondensedNodes, 'canReachDeathAfterSteps');
  populatePessimisticTimes(condensedNodeIdsContainingEndMissionActions, condensedActionGraph, childCondensedNodesToParentCondensedNodes, 'pessimisticDurationUntilEnd', 'canReachEndAfterSteps');
  populatePessimisticTimes(condensedNodeIdsContainingDeathActions, condensedActionGraph, childCondensedNodesToParentCondensedNodes, 'pessimisticDurationUntilDeath', 'canReachDeathAfterSteps');
  return {
    condensedActionGraph: toCondensedActionGraph(condensedActionGraph, startingCondensedNodeId),
    actionGraph: toActionGraph(actionGraph, actionsNotInGraph, referencedActionsThatDoNotExist)
  };
}

/**
 * populateMinStepsToTargetActions - populates the (minimum amount of) steps it takes to reach the target actions in the graph.
 *
 * @param {IntermediateActionGraph<IdType>} actionGraph - the action graph to traverse
 * @param {string[]} targetActions - the actions to count the steps to
 * @param {ChildToParentActionOutcomes} childActionToParentActionsMapping - a mapping of the child action to the outcomes of its parent actions (used to traverse the action graph backwards)
 * @param {'canReachEndAfterSteps'|'canReachDeathAfterSteps'} stepsFromTargetProperty - the property on the IntermediateActionGraphNode where the information should be populated
 *
 * @return {void}
 */
function populateMinStepsToTargetActions(actionGraph, targetActions, childActionToParentActionsMapping, stepsFromTargetProperty) {
  const actionsThatWillReachTargetActionsQueue = targetActions.map(actionId => ({
    actionId,
    stepsFromTarget: 0,
    closestTo: actionId
  }));
  const alreadyHandledActionsWhichReachTargetActions = new Set(targetActions);

  // breadth first search to populate which actions can reach any of the target nodes
  let currentItem;
  while (currentItem = actionsThatWillReachTargetActionsQueue.pop()) {
    const childAction = actionGraph[currentItem.actionId];
    if (childAction && childAction[stepsFromTargetProperty] == null) {
      childAction[stepsFromTargetProperty] = {
        stepCount: currentItem.stepsFromTarget,
        actionId: currentItem.closestTo
      };
    }
    const parentActionIds = Object.keys(childActionToParentActionsMapping[currentItem.actionId]);
    for (const parentActionId of parentActionIds) {
      if (alreadyHandledActionsWhichReachTargetActions.has(parentActionId)) {
        continue;
      }
      const parentAction = actionGraph[parentActionId];
      if (!parentAction) {
        continue;
      }
      const parentOutcomesContainingChild = childActionToParentActionsMapping[currentItem.actionId][parentActionId];
      let allOutcomesReachEnd = true;
      for (const outcome of parentAction.distinctOutcomes) {
        if (parentOutcomesContainingChild.has(outcome.outcomeId)) {
          outcome[stepsFromTargetProperty] = {
            stepCount: currentItem.stepsFromTarget,
            actionId: currentItem.closestTo
          };
        } else if (outcome[stepsFromTargetProperty] == null) {
          allOutcomesReachEnd = false;
        }
      }
      if (allOutcomesReachEnd) {
        // only ignore adding this action back to the queue if all outcomes of this action are already handled
        alreadyHandledActionsWhichReachTargetActions.add(parentActionId);
      }
      actionsThatWillReachTargetActionsQueue.push({
        actionId: parentActionId,
        stepsFromTarget: currentItem.stepsFromTarget + 1,
        closestTo: currentItem.closestTo
      });
    }
  }
}

/**
 * populateMinStepsToTargetCondensedNodes - populates the (minimum amount of) steps it takes to reach the target condensed nodes in the graph.
 *
 * @param {string[]} targetNodes - the nodes to count the steps to
 * @param {IntermediateCondensedGameActionGraph<IdType>} condensedActionGraph - the graph to traverse
 * @param {ChildToParentCondensedNodes} childToParentsMap - a mapping of the child condensedNodes to their parents (used to traverse the action graph backwards)
 * @param {'canReachEndAfterSteps'|'canReachDeathAfterSteps'} populatingField - the field on the IntermediateCondensedGameActionGraphNode where the information should be populated
 * @param {'breakOnEndPath'} skipEdgeRule? - if this field is set, any edge containing this rule will be completely ignored by this algorithm.
 *
 * @return {void}
 */
function populateMinStepsToTargetCondensedNodes(targetNodes, condensedActionGraph, childToParentsMap, populatingField, skipEdgeRule) {
  const condensedNodesWhichCanReachTargetNodes = new _fifoQueue.FIFOQueue(targetNodes.map(condensedNodeId => ({
    condensedNodeId,
    stepsFromTargetAction: 0,
    closestTo: condensedNodeId
  })));
  const seenCondensedNodeIds = new Set(targetNodes);
  let nextCondensedNodeId;
  while (nextCondensedNodeId = condensedNodesWhichCanReachTargetNodes.shift()) {
    const condensedNode = condensedActionGraph[nextCondensedNodeId.condensedNodeId];
    condensedNode[populatingField] ??= {
      stepCount: nextCondensedNodeId.stepsFromTargetAction,
      actionId: nextCondensedNodeId.closestTo
    };
    const condensedNodeParents = childToParentsMap[nextCondensedNodeId.condensedNodeId];
    if (!condensedNodeParents) {
      continue;
    }
    for (let parentData of condensedNodeParents) {
      if (seenCondensedNodeIds.has(parentData.parentNodeId)) {
        continue;
      }
      seenCondensedNodeIds.add(parentData.parentNodeId);
      if (skipEdgeRule && parentData.specialRules?.[skipEdgeRule]) {
        continue;
      }
      condensedNodesWhichCanReachTargetNodes.push({
        condensedNodeId: parentData.parentNodeId,
        stepsFromTargetAction: nextCondensedNodeId.stepsFromTargetAction + 1,
        closestTo: nextCondensedNodeId.closestTo
      });
    }
  }
}
/**
 * populatePessimisticTimes - populates the maximum amount of time needed to reach the target nodes from each node in the condensed graph.
 * Note: the field described in `stepCountField` and the depthFromStart field MUST ALREADY BE POPULATED.
 *
 * This algorithm works by using modified breadth first search from the target nodes, traversing the graph backwards, and picking an edge using the child with the
 * maximum duration to one of the target nodes. In the situation in which all pending nodes have children which required fields are not populated, we will pick the
 * pending node furthest away from the start (as it is more likely other nodes depend on it) and with the highest current pessimistic time and force it to populate
 * with the data we do currently have.
 *
 * @param {string[]} targetNodes - the nodes to determine the duration to
 * @param {IntermediateCondensedGameActionGraph<IdType>} condensedActionGraph - the graph to traverse
 * @param {ChildToParentCondensedNodes} childToParentsMap - a mapping of the child condensedNodes to their parents (used to traverse the action graph backwards)
 * @param {'canReachEndAfterSteps'|'canReachDeathAfterSteps'} populatingField - the field on the IntermediateCondensedGameActionGraphNode where the information should be populated
 * @param {'canReachEndAfterSteps' | 'canReachDeathAfterSteps'} stepCountField - the field (MUST ALREADY BE POPULATED) which describes the minimum amount of steps to the target nodes. This field
 *    is used to determine if there is a loop or if a condensed node will never reach a target node.
 *
 * @return {void}
 */
function populatePessimisticTimes(targetNodes, condensedActionGraph, childToParentsMap, fieldToPopulate, stepCountField) {
  const handledCondensedNodeIds = new Set(targetNodes);
  const condensedNodesWhichCanReachTargetNodes = new _fifoQueue.FIFOQueue(); // multiple of the same nodeIds can be in here at the same time
  for (let targetNodeId of targetNodes) {
    const targetNodeParents = childToParentsMap[targetNodeId];
    if (!targetNodeParents) {
      continue;
    }
    for (let parentData of targetNodeParents) {
      if (handledCondensedNodeIds.has(parentData.parentNodeId)) {
        continue;
      }
      condensedNodesWhichCanReachTargetNodes.push(parentData.parentNodeId);
    }
  }
  const nodesWithMissingChildren = (0, _heap.generateMinHeap)([], (a, b) => {
    if (a.depthFromStart !== b.depthFromStart) {
      // the furthest depth from start should come first
      return b.depthFromStart - a.depthFromStart;
    }
    // the greatest duration should come first
    return b.currentPessimisticTime - a.currentPessimisticTime;
  }); // each entry represents a node which contains a child which did not have the needed fields populated. The same node can be represented multiple times in this heap.

  while (true) {
    let nextCondensedNodeId = condensedNodesWhichCanReachTargetNodes.shift();
    let isForced = false;
    if (!nextCondensedNodeId) {
      // We ran out of nodes to check, move on to forcing a node with at least one child with required populated field to populate.
      // Note: this can happen if there are multiple intersecting cycles in the graph.
      // Note: if the node has already been populated then this loop will short circuit and pick the next node in the heap.
      nextCondensedNodeId = nodesWithMissingChildren.pop()?.nodeId;
      isForced = true;
    }
    if (!nextCondensedNodeId) {
      break;
    }
    const condensedNode = condensedActionGraph[nextCondensedNodeId];
    const minStepsTillTargetNodeData = condensedNode?.[stepCountField];
    if (!condensedNode || handledCondensedNodeIds.has(nextCondensedNodeId) || condensedNode[fieldToPopulate] ||
    // already handled
    minStepsTillTargetNodeData == null) {
      // node was either already handled or will never be handled
      handledCondensedNodeIds.add(nextCondensedNodeId);
      continue;
    }
    let maximumChildDuration = null;
    let allChildPessimisticDurationsPopulated = true;
    for (let childNodeEdge of condensedNode.childNodes) {
      if (childNodeEdge.childNodeId === nextCondensedNodeId) {
        continue; // node loops right back to itself, ignore itself as a child
      }
      const childNode = condensedActionGraph[childNodeEdge.childNodeId];
      if (!childNode || childNode[stepCountField] == null) {
        // child can not reach any of the target nodes, so it will never have this field populated
        continue;
      }
      const childDurationField = childNode[fieldToPopulate];
      if (childDurationField == null) {
        allChildPessimisticDurationsPopulated = false;
        continue; // have to `continue` instead of `break` to make sure `maximumChildDuration` is correct when adding this node to the `nodesWithMissingChildren` heap
      }
      if (maximumChildDuration != null && maximumChildDuration.duration < childDurationField.duration) {
        continue;
      }

      // use the longest duration
      maximumChildDuration = {
        duration: childDurationField.duration,
        nextNodeId: childNodeEdge.childNodeId
      };
    }
    if (!allChildPessimisticDurationsPopulated && !isForced) {
      // this node will be checked later when its other children get populated
      if (!maximumChildDuration) {
        // as we had to visit a child to get to this point, this situation should not be possible
        continue;
      }
      nodesWithMissingChildren.push({
        nodeId: nextCondensedNodeId,
        currentPessimisticTime: maximumChildDuration.duration,
        depthFromStart: condensedNode.depthFromStart
      });
      continue;
    }
    condensedNode[fieldToPopulate] ??= maximumChildDuration ? {
      duration: maximumChildDuration.duration + condensedNode.duration,
      nextNodeId: maximumChildDuration.nextNodeId
    } : null; // ASSUMPTION: duration being null means that there is no way for the node to reach any of the target node
    handledCondensedNodeIds.add(nextCondensedNodeId);
    const condensedNodeParents = childToParentsMap[nextCondensedNodeId];
    if (!condensedNodeParents) {
      continue;
    }
    for (let parentData of condensedNodeParents) {
      if (handledCondensedNodeIds.has(parentData.parentNodeId)) {
        continue;
      }
      condensedNodesWhichCanReachTargetNodes.push(parentData.parentNodeId);
    }
  }
}

/**
 * minOfOptionalStepsToAction - get the minimum value of two optional numbers. `null/undefined` is considered Infinity. If both values are `null/undefined`, then the result is `null`.
 *
 * @param {StepsToAction|null|undefined} val1 - a value to compare
 * @param {StepsToAction|null|undefined} val2 - a value to compare
 *
 * @return {StepsToAction | null} - number if there is a minimum, null if there is no possible minimum
 */
function minOfOptionalStepsToAction(val1, val2) {
  if (val1 == null) {
    return val2 ?? null;
  }
  if (val2 == null) {
    return val1 ?? null;
  }
  if (val1.stepCount < val2.stepCount) {
    return val1;
  }
  return val2;
}

/**
 * toActionGraph - convert the intermediate representation of an action graph to an action graph
 *
 * @param {IntermediateActionGraph<IdType>} intermediateActionGraph - the intermediate representation of the action graph
 * @param {Set<string>} actionsNotInGraph - a set of actions which do not exist as nodes in the graph (ie. there is no path connecting them to the starting action)
 * @param {Set<string>} actionsReferencedWhichDoNotExist - a set of actionIds which are referenced by other actions but do not have an action with the id
 *
 * @return {ActionGraph<IdType>} - the action graph
 */
function toActionGraph(intermediateActionGraph, actionsNotInGraph, actionsReferencedWhichDoNotExist) {
  let maxDepthFromStart = 0;
  const linkDeDuplicator = {};
  const nodes = [];
  Object.keys(intermediateActionGraph).forEach(actionId => {
    const actionNode = intermediateActionGraph[actionId];
    linkDeDuplicator[actionId] ??= {
      source: actionNode.actionId,
      targets: {}
    };
    const targets = linkDeDuplicator[actionId].targets;
    actionNode.distinctOutcomes.forEach(outcome => {
      outcome.actionData.forEach(childActionData => {
        const childAction = intermediateActionGraph[String(childActionData.actionId)];
        targets[String(childActionData.actionId)] ??= {
          target: childActionData.actionId,
          minStepsUntilEnd: null,
          minStepsUntilDeath: null,
          linkNames: new Set()
        };
        const actionTargets = targets[String(childActionData.actionId)];
        actionTargets.linkNames.add(childActionData.groupId);
        actionTargets.minStepsUntilEnd = childAction?.canReachEndAfterSteps != null ? minOfOptionalStepsToAction(actionTargets.minStepsUntilEnd, outcome.canReachEndAfterSteps) : null;
        actionTargets.minStepsUntilDeath = childAction?.canReachDeathAfterSteps != null ? minOfOptionalStepsToAction(actionTargets.minStepsUntilDeath, outcome.canReachDeathAfterSteps) : null;
      });
    });
    nodes.push({
      id: actionNode.actionId,
      depthFromStart: actionNode.depthFromStart,
      minStepsTillEnd: actionNode.canReachEndAfterSteps ?? null,
      minStepsTillDeath: actionNode.canReachDeathAfterSteps ?? null
    });
    maxDepthFromStart = Math.max(maxDepthFromStart, actionNode.depthFromStart);
  });
  const links = [];
  Object.keys(linkDeDuplicator).forEach(sourceStringActionId => {
    const deDuplicatorSource = linkDeDuplicator[sourceStringActionId];
    Object.keys(deDuplicatorSource.targets).forEach(targetStringActionId => {
      const targets = deDuplicatorSource.targets[targetStringActionId];
      links.push({
        source: deDuplicatorSource.source,
        linkNames: Array.from(targets.linkNames),
        target: deDuplicatorSource.targets[targetStringActionId].target,
        minStepsTillEnd: targets.minStepsUntilEnd,
        minStepsTillDeath: targets.minStepsUntilDeath
      });
    });
  });
  return {
    links,
    nodes,
    maxDepthFromStart,
    actionsNotInGraph: Array.from(actionsNotInGraph),
    actionsReferencedWhichDoNotExist: Array.from(actionsReferencedWhichDoNotExist)
  };
}

/**
 * toCondensedActionGraph - takes the intermediate condensed action graph and creates the final condensed action graph
 *
 * @param {IntermediateCondensedGameActionGraph<IdType>} intermediateCondensedGameActionGraph - the graph to convert to the final condensedActionGraph
 * @param {string} startingNodeId - the id of the condensed node which is active as soon as the simulation starts
 *
 * @return {CondensedActionGraph<IdType>} - the final condensedActionGraph
 */
function toCondensedActionGraph(intermediateCondensedGameActionGraph, startingNodeId) {
  let maxDepthFromStart = 0;
  const criticalActionIdToCondensedNodeIds = {};
  const links = [];
  const nodes = [];
  Object.keys(intermediateCondensedGameActionGraph).forEach(nodeId => {
    const condensedNode = intermediateCondensedGameActionGraph[nodeId];
    condensedNode.childNodes.forEach(({
      childNodeId,
      specialRules
    }) => {
      links.push({
        source: nodeId,
        target: childNodeId,
        specialRules
      });
    });
    const meta = {
      isStart: condensedNode.isStartingNode,
      stepsFromStart: condensedNode.depthFromStart
    };
    if (condensedNode.canReachEndAfterSteps != null) {
      meta.stepsFromEnd = condensedNode.canReachEndAfterSteps;
    }
    if (condensedNode.containsEndAction) {
      meta.containsEndAction = condensedNode.containsEndAction;
    }
    if (condensedNode.containsDeathAction) {
      meta.containsDeathAction = condensedNode.containsDeathAction;
    }
    if (condensedNode.canReachDeathAfterSteps != null) {
      meta.stepsFromDeath = condensedNode.canReachDeathAfterSteps;
    }
    if (condensedNode.pessimisticDurationUntilDeath) {
      meta.pessimisticDurationUntilDeath = condensedNode.pessimisticDurationUntilDeath;
    }
    if (condensedNode.pessimisticDurationUntilEnd != null) {
      meta.pessimisticDurationUntilEnd = condensedNode.pessimisticDurationUntilEnd;
    }
    for (let actionId in condensedNode.criticalPathActions) {
      criticalActionIdToCondensedNodeIds[actionId] ??= [];
      criticalActionIdToCondensedNodeIds[actionId].push(nodeId);
    }
    nodes.push({
      id: nodeId,
      actions: condensedNode.actions,
      notableEvents: condensedNode.notableEvents,
      criticalPathActions: condensedNode.criticalPathActions,
      meta,
      duration: condensedNode.duration,
      children: condensedNode.childNodes
    });
    maxDepthFromStart = Math.max(maxDepthFromStart, condensedNode.depthFromStart);
  });
  return {
    links,
    nodes,
    maxDepthFromStart,
    startingNodeId,
    criticalActionIdToCondensedNodeIds
  };
}

/**
 * getCondensedNodeId - get the id of a condensed node
 *
 * @param {string} fromAction - the action that is causing the outcome in the simulation
 * @param {string} outcomeId - the id of the outcome of the action the simulation is taking
 *
 * @return {string} - an id representing the outcome in the simulation from the given action
 */
function getCondensedNodeId(fromAction, outcomeId) {
  return `${fromAction}-${outcomeId}`;
}

/**
 * getStartingCondensedNodeId - get the id of the starting node in the condensed action graph
 *
 * @param {string} startingActionId - the id of the starting action of the simulation
 *
 * @return {string} - the id of the starting condensed action node in the graph
 */
function getStartingCondensedNodeId(startingActionId) {
  return `simulation-start-${startingActionId}`;
}

/**
 * removedPendingActionsSpawnedByAction - remove actions spawned by the given action(Id) from the pendingActions
 *
 * @param {IdType} removeActionsSpawnedBy - any pending action spawned by this action will be removed
 * @param {Heap<ActionDataWithStartTimeAndSpawner<IdType>>} pendingActions - [MUTATES] the actions still pending to be handled on a condensedNode
 *
 */
function removedPendingActionsSpawnedByAction(removeActionsSpawnedBy, pendingActions) {
  const currentlyPendingActions = pendingActions.toArray();
  pendingActions.clear();
  for (let pendingAction of currentlyPendingActions) {
    if (pendingAction.spawnedByAction === removeActionsSpawnedBy) {
      continue;
    }
    pendingActions.push(pendingAction);
  }
}

/**
 * updateCurrentScreenAction - update the current screen action in the condensedActionGraph graphState. Also removes all pending actions spawned by the previous
 * screen action (if one exists). This is to match the functionality of mission-server.
 *
 * @param {IdType} runningActionId - the id of the action being handled
 * @param {GraphState<IdType>} graphState - the current GraphState
 * @param {Heap<ActionDataWithStartTimeAndSpawner<IdType>>} pendingActions - the actions still pending to be handled on the condensedNode
 */
function updateCurrentScreenAction(runningActionId, graphState, pendingActions) {
  const previousScreenActionId = graphState.currentScreenActionId;
  graphState.currentScreenActionId = runningActionId;
  if (!previousScreenActionId) {
    return;
  }
  removedPendingActionsSpawnedByAction(previousScreenActionId, pendingActions);
}

// a mapping of an actions type to a function which update the GraphState of a condensedNode when it is handled
const ACTION_TO_CONDENSED_STATE_UPDATER_MAP = {
  VIDEO_SCREEN: updateCurrentScreenAction,
  IMAGE_SCREEN: updateCurrentScreenAction,
  NAVIGATION_SCREEN: updateCurrentScreenAction,
  CULMINATING_MOMENT_SCREEN: updateCurrentScreenAction,
  MAP_SCREEN: updateCurrentScreenAction,
  QUESTION_SLOT_SCREEN: updateCurrentScreenAction,
  COLLABORATIVE_CULMINATING_MOMENT_SCREEN: updateCurrentScreenAction
};